import type {
  IClassStudent,
  IClassDetailsLesson,
  IClassDetailsStudent,
  IClassDetailsTeacher,
  IClassSummaryTeacher,
} from '@/types/class';

import { enqueueSnackbar } from 'notistack';
import { Activity, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useLocation, useNavigate } from 'react-router-dom';

import { IconBook, IconMail, IconTrash, IconSearch } from '@tabler/icons-react';

import { UserAvatarList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import Input from '@/components/Input/Input';
import ProgressBar from '@/components/ProgressBar';
import ClassDetailsSkeleton from '@/components/SkeletonLoader/ClassDetailsSkeleton';
import CustomTable from '@/components/Table';
import TagCapsule from '@/components/TagCapsule';
import { LessonStatusConfig } from '@/constants/constant';
import { ELessonStatus } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import { useRelativeTime } from '@/hooks/useRelativeTime';
import { StudentDetailDrawer } from '@/pages/school/classDetails/components/StudentDetailDrawer';
import {
  removeClass,
  getClassStudents,
  getClassTeacherAndSummary,
} from '@/service/classService';
import { useSettingsStore } from '@/store/useSettingsStore';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import { getAssetUrl } from '@/utils';

import AddStudentMenuButton from './components/AddStudentMenuButton';
import { AddStudentModal } from './components/AddStudentModal';
import { BulkUploadStudentsModal } from './components/BulkUploadStudentsModal';

// ─── Component ───────────────────────────────────────────────────────────────

const ClassDetails = () => {
  const { formatText } = useFormatText();
  const { fromNow } = useRelativeTime();
  const { setIsBreadcrumb } = usePageTitle();
  const navigate = useNavigate();
  const { classId } = useParams<{ classId: string }>();
  const location = useLocation();

  const [currentPage, setCurrentPage] = useState(1);
  const [teachers, setTeachers] = useState<IClassDetailsTeacher[]>([]);
  const [teacherCount, setTeacherCount] = useState(0);

  const [lessons, setLessons] = useState<IClassDetailsLesson[]>([]);
  const [totalLessons, setTotalLessons] = useState(0);
  const [students, setStudents] = useState<IClassDetailsStudent[]>([]);
  const [studentsTotal, setStudentsTotal] = useState(0);
  const { isScreenLoading, setScreenLoading, isTableLoading, setTableLoading } =
    useSkeletonStore();
  const [studentsRefreshKey, setStudentsRefreshKey] = useState(0);
  const [studentSortField, setStudentSortField] = useState('');
  const [studentSortDir, setStudentSortDir] = useState('');
  const [isAddStudentModalOpen, setIsAddStudentModalOpen] = useState(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] =
    useState<IClassDetailsStudent | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { settings } = useSettingsStore();
  const [isRemoveClassModalOpen, setIsRemoveClassModalOpen] = useState(false);

  const { register, watch } = useForm({
    defaultValues: {
      classSearch: '',
    },
  });

  const searchQuery = watch('classSearch');
  const pageSize = 10;
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const className =
    (location.state as { className?: string } | null)?.className || 'Class';

  const breadcrumbPath = [
    {
      label: formatText('breadcrumb.teachers'),
      to: `/school/teachers-classes?tab=classes`,
    },
    {
      label: className,
      to: classId ? `/school/teachers-classes/class-details/${classId}` : '',
    },
  ];

  useEffect(() => {
    setIsBreadcrumb(breadcrumbPath);
    return () => {
      setIsBreadcrumb([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, className]);

  useEffect(() => {
    const fetchClassTeacherAndSummary = async () => {
      if (!classId) return;

      setScreenLoading(true);
      try {
        const res = await getClassTeacherAndSummary(classId);

        if (res?.status && res?.data) {
          const teacherData = Array.isArray(res.data.teachers)
            ? (res.data.teachers as IClassSummaryTeacher[])
            : [];

          const lessonData = Array.isArray(res.data.lesson_breakdown)
            ? (res.data.lesson_breakdown as Partial<IClassDetailsLesson>[])
            : [];

          setTeacherCount(res.data.total_teachers ?? 0);
          setTotalLessons(res.data.total_lessons ?? lessonData.length);

          setTeachers(
            teacherData.map((teacher) => ({
              id: teacher.id,
              name: teacher.name,
              email: teacher.email,
              lessons_created_count: teacher.lessons_created_count,
              avatar: getAssetUrl(teacher?.profile_pic) || undefined,
            }))
          );

          setLessons(
            lessonData.map((lesson, index: number) => ({
              id: String(lesson.id || index),
              title: lesson.title || '-',
              completed: Number(lesson.completed || 0),
              total: Number(lesson.total || 0),
              status: lesson.status || ELessonStatus.IN_PROGRESS,
            }))
          );
        } else {
          setTeachers([]);
          setLessons([]);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setScreenLoading(false);
      }
    };

    fetchClassTeacherAndSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery]);

  const handleStudentSort = (key: string) => {
    setCurrentPage(1);
    if (studentSortField === key) {
      if (studentSortDir === 'ASC') setStudentSortDir('DESC');
      else if (studentSortDir === 'DESC') {
        setStudentSortField('');
        setStudentSortDir('');
      } else setStudentSortDir('ASC');
    } else {
      setStudentSortField(key);
      setStudentSortDir('ASC');
    }
  };

  useEffect(() => {
    const fetchStudents = async () => {
      if (!classId) return;

      setTableLoading(true);
      try {
        const query: Record<string, unknown> = {
          limit: pageSize,
          offset: (currentPage - 1) * pageSize,
        };

        if (studentSortField && studentSortDir) {
          query[`order[${studentSortField}]`] = studentSortDir;
        } else {
          query.created_at = 'DESC';
        }

        if (debouncedSearchQuery) {
          query.search = debouncedSearchQuery;
        }

        const res = await getClassStudents(classId, query);

        if (res?.status && Array.isArray(res.data)) {
          const mappedStudents: IClassDetailsStudent[] = res.data.map(
            (student: IClassStudent) => ({
              id: student.id,
              student_name: student.student_name,
              profile_pic: getAssetUrl(student?.profile_pic) || undefined,
              lessons_count: student.lessons_count,
              progress: student.progress,
              accuracy: student.accuracy,
              status: student.status,
              last_active: student.last_active
                ? Number(student.last_active)
                : null,
            })
          );

          setStudents(mappedStudents);
          setStudentsTotal(res.total ?? mappedStudents.length);
        } else {
          setStudents([]);
          setStudentsTotal(0);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setTableLoading(false);
      }
    };

    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    classId,
    currentPage,
    debouncedSearchQuery,
    studentSortField,
    studentSortDir,
    studentsRefreshKey,
  ]);

  // Handle row click
  const handleRowClick = (id: string) => {
    const student = students.find((s) => s.id === id);
    if (student) {
      setSelectedStudent(student);
      setIsDrawerOpen(true);
    }
  };

  // Handle drawer close
  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedStudent(null);
  };

  // Handle remove class
  const handleRemoveClass = async () => {
    try {
      const response = await removeClass(classId || '');

      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });

        setIsRemoveClassModalOpen(false);
        navigate(`/school/teachers-classes?tab=classes`);
      } else {
        enqueueSnackbar(response?.message, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'classDetails.table.studentName',
      key: 'student_name',
      sorter: true,
      handleSort: () => handleStudentSort('student_name'),
      render: (record: IClassDetailsStudent) => (
        <div className="flex items-center gap-3">
          <UserAvatarList
            user={record.student_name}
            image={record.profile_pic || ''}
            className="w-10 h-10"
          />
          <div className="flex flex-col">
            <p className="text-sm font-semibold text-neutral-primary">
              {record?.student_name}
            </p>
          </div>
        </div>
      ),
    },
    {
      title: 'classDetails.table.lessons',
      key: 'lessons_count',
      sorter: true,
      handleSort: () => handleStudentSort('lessons_count'),
      render: (record: IClassDetailsStudent) => (
        <div className="inline-flex items-center justify-center px-2 py-0.5 rounded-full bg-pink-50 min-w-6">
          <span className="text-xs font-semibold text-pink-600">
            {record?.lessons_count}
          </span>
        </div>
      ),
    },
    {
      title: 'classDetails.table.progress',
      key: 'progress',
      sorter: true,
      handleSort: () => handleStudentSort('progress'),
      render: (record: IClassDetailsStudent) => (
        <div className="w-full max-w-93">
          <ProgressBar
            value={record?.progress ?? 0}
            showPercentage={true}
            height="h-2"
          />
        </div>
      ),
    },
    {
      title: 'classDetails.table.accuracy',
      key: 'accuracy',
      sorter: true,
      handleSort: () => handleStudentSort('accuracy'),
      render: (record: IClassDetailsStudent) => (
        <span className="text-sm text-neutral-primary">
          {record?.accuracy ?? 0}%
        </span>
      ),
    },
    {
      title: 'classDetails.table.status',
      key: 'status',
      sorter: true,
      handleSort: () => handleStudentSort('status'),
      render: (record: IClassDetailsStudent) => {
        const statusKey = record?.status as ELessonStatus;
        const config = LessonStatusConfig[statusKey];

        return config ? (
          <TagCapsule
            value={formatText(config.label)}
            className={config.className}
          />
        ) : (
          <span>-</span>
        );
      },
    },
    {
      title: 'classDetails.table.lastActive',
      key: 'last_active',
      sorter: true,
      handleSort: () => handleStudentSort('last_active'),
      render: (record: IClassDetailsStudent) => (
        <span className="text-sm text-neutral-secondary">
          {record.last_active ? fromNow(record.last_active) : '-'}
        </span>
      ),
    },
  ];

  if (isScreenLoading) return <ClassDetailsSkeleton />;

  return (
    <div className="space-y-6" data-testid="class-details-container">
      {/* Search & Filters */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 ps-1">
          <Input
            name="classSearch"
            register={register}
            placeholder={formatText('classDetails.search.placeholder')}
            leftIcon={{
              icon: <IconSearch size={18} className="text-neutral-500" />,
            }}
            className="w-full max-w-md"
            inputClassName="h-12"
            dataTestId="student-search-input"
          />
        </div>
        <div className="flex items-center gap-4">
          <Button
            label={formatText('classDetails.actions.removeClass')}
            icon={<IconTrash size={20} />}
            variant="outline"
            desructive
            onClick={() => setIsRemoveClassModalOpen(true)}
            className="bg-brand-50! text-brand-secondary! border-brand-50!"
            dataTestId="remove-class-button"
          />
          <AddStudentMenuButton
            onSingleStudent={() => setIsAddStudentModalOpen(true)}
            onBulkUpload={() => setIsBulkUploadModalOpen(true)}
            dataTestId="add-student-menu"
          />
        </div>
      </div>

      {/* Teachers Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-neutral-primary">
              {formatText('classDetails.teachers.title')}
            </h2>
            <div
              className="inline-flex items-center px-2 py-0.5 rounded-full bg-neutral-100"
              data-testid="teachers-count"
            >
              <span className="text-xs font-semibold text-neutral-primary">
                {String(teacherCount).padStart(2, '0')}
              </span>
            </div>
          </div>
          {teacherCount > 4 && (
            <Button
              label={formatText('common.actions.view-all')}
              variant="outline"
              onClick={() =>
                navigate(
                  `/school/teachers-classes/class-details/${classId}/teachers`,
                  {
                    state: { className: breadcrumbPath[1].label },
                  }
                )
              }
              dataTestId="view-all-teachers-button"
            />
          )}
        </div>

        {teachers?.length === 0 ? (
          <div className="py-10 text-center" data-testid="teachers-empty">
            <p className="text-sm text-neutral-secondary">
              {formatText('classDetails.teachers.empty')}
            </p>
          </div>
        ) : (
          <div className="flex items-center flex-wrap justify-start gap-6">
            {teachers?.map((teacher) => (
              <div
                key={teacher.id}
                data-testid={`teacher-card-${teacher.id}`}
                className="border border-neutral-200 rounded-2xl p-4 flex-1 space-y-4 cursor-pointer hover:border-neutral-200 hover:shadow-small transition"
                role="button"
                tabIndex={0}
                onClick={() =>
                  navigate(
                    `/school/teachers-classes/teacher-details/${teacher.id}`
                  )
                }
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(
                      `/school/teachers-classes/teacher-details/${teacher.id}`
                    );
                  }
                }}
              >
                <div className="flex items-center gap-4">
                  <UserAvatarList
                    user={teacher.name}
                    image={teacher.avatar || ''}
                    className="w-12 h-12"
                  />
                  <h3 className="text-xl font-bold text-neutral-primary leading-7 tracking-tight">
                    {teacher.name}
                  </h3>
                </div>

                <div className="flex gap-2">
                  <IconMail size={20} className="text-neutral-secondary" />
                  <div className="flex flex-col gap-1">
                    <p className="text-sm text-neutral-secondary">
                      {formatText('classDetails.teachers.email')}
                    </p>
                    <p className="text-base text-neutral-primary">
                      {teacher.email}
                    </p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <IconBook size={20} className="text-neutral-secondary" />
                  <div className="flex flex-col gap-1">
                    <p className="text-sm text-neutral-secondary">
                      {formatText('classDetails.teachers.lessonsCreated')}
                    </p>
                    <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-pink-50 w-fit">
                      <span className="text-xs font-semibold text-pink-600">
                        {teacher.lessons_created_count}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lesson Breakdown Section */}
      {lessons.length > 0 && (
        <div className="space-y-4" data-testid="lesson-breakdown-section">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-neutral-primary">
                {formatText('classDetails.lessons.title')}
              </h2>
              <div
                className="inline-flex items-center px-2 py-0.5 rounded-full bg-neutral-100"
                data-testid="lessons-count"
              >
                <span className="text-xs font-semibold text-neutral-primary">
                  {String(totalLessons).padStart(2, '0')}
                </span>
              </div>
            </div>
            {totalLessons > 4 && (
              <Button
                label={formatText('common.actions.view-all')}
                variant="outline"
                onClick={() =>
                  navigate(
                    `/school/teachers-classes/class-details/${classId}/lesson-breakdown`,
                    { state: { className: breadcrumbPath[1].label } }
                  )
                }
                dataTestId="view-all-lessons-button"
              />
            )}
          </div>

          <div
            className="flex items-center flex-wrap justify-start gap-4"
            data-testid="lessons-list"
          >
            {lessons.map((lesson) => {
              const statusKey = lesson.status as ELessonStatus;
              const config = LessonStatusConfig[statusKey];
              return (
                <div
                  key={lesson.id}
                  data-testid={`lesson-card-${lesson.id}`}
                  className="border border-neutral-200 rounded-2xl p-4 flex-1 h-full flex items-center"
                >
                  <div className="flex items-center justify-between h-full w-full">
                    <div className="flex flex-col gap-0.5">
                      <h3 className="text-base font-semibold whitespace-nowrap text-neutral-primary">
                        {lesson.title}
                      </h3>
                      <p className="text-sm text-neutral-secondary">
                        {lesson.completed}/{lesson.total}{' '}
                        {formatText('classDetails.lessons.completed')}
                      </p>
                    </div>
                    {config && (
                      <TagCapsule
                        value={formatText(config?.label ? config?.label : '')}
                        className={config.className ?? ''}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Student Progress Table */}
      <div className="space-y-4" data-testid="student-progress-section">
        <h2 className="text-lg font-semibold text-neutral-primary">
          {formatText('classDetails.students.title')}
        </h2>
        <CustomTable
          dataSource={students}
          columns={columns}
          total={studentsTotal}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          isLoading={isTableLoading}
          fieldName={studentSortField}
          sortDirection={studentSortDir}
          rowKey="id"
          onRowClick={handleRowClick}
          dataTestId="student-progress-table"
        />
      </div>

      {/* Add Student Modal */}
      <Activity mode={isAddStudentModalOpen ? 'visible' : 'hidden'}>
        <AddStudentModal
          isOpen={isAddStudentModalOpen}
          onClose={() => setIsAddStudentModalOpen(false)}
          onSuccess={() => setStudentsRefreshKey((prev) => prev + 1)}
          defaultClassId={classId}
        />
      </Activity>

      {/* Bulk Upload Students Modal */}
      <Activity mode={isBulkUploadModalOpen ? 'visible' : 'hidden'}>
        <BulkUploadStudentsModal
          isOpen={isBulkUploadModalOpen}
          onClose={() => setIsBulkUploadModalOpen(false)}
          classId={classId ?? ''}
          onSuccess={() => setStudentsRefreshKey((prev) => prev + 1)}
        />
      </Activity>

      {/* Student Detail Drawer */}
      <Activity mode={isDrawerOpen ? 'visible' : 'hidden'}>
        <StudentDetailDrawer
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
          student={selectedStudent}
        />
      </Activity>

      {/* Remove Class Confirmation Modal */}
      <Activity mode={isRemoveClassModalOpen ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={isRemoveClassModalOpen}
          setIsOpen={setIsRemoveClassModalOpen}
          title="teachers-classes.remove-class.title"
          descriptionPrefix={formatText('common.confirmation.remove-prefix')}
          descriptionSuffix="teachers-classes.remove-class.suffix"
          entityName="Class 1A"
          confirmLabel={formatText('teachers-classes.remove-class.confirm')}
          onConfirm={handleRemoveClass}
          isDestructive
        />
      </Activity>
    </div>
  );
};

export default ClassDetails;
